import { commissionAdvanceHelp } from '@hue/contracts';
import {
  BackToTop,
  Button,
  DatePicker,
  HelpTip,
  IconButton,
  Input,
  Modal,
  Select,
  StatusPill,
  formatCivilDate,
  formatCurrency,
  useSnackbar,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { HandCoins, ReceiptText } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import {
  OperationalPeriodContext,
  type OperationalMonth,
} from '../../app/operational-period-context.js';
import { CommissionAdvanceDialog } from '../../app/commission-advance-dialog.js';
import {
  EmptyCard,
  ErrorCard,
  ListFilterBar,
  LoadingCard,
  RequestAlert,
  SortableColumn,
} from '../../app/data-view.js';
import { PageHeader } from '../../app/page-title.js';
import {
  commissionsApi,
  getApiError,
  type CommissionBalance,
  type CommissionClosingLine,
  type CommissionClosingSummary,
  type CommissionClosingView,
} from '../../lib/api.js';
import { commissionClosingCsv } from './commission-closing-export.js';
import { accountLabelOf, monthLabel, monthOptions } from './commission-closing-labels.js';

type SortKey = 'name' | 'commission' | 'advances' | 'net' | 'paid' | 'outstanding';

const brl = (cents: number) => formatCurrency(cents, 'BRL');

function useCommissionMonth(today: string) {
  const period = useContext(OperationalPeriodContext);
  const [localMonth, setLocalMonth] = useState(today.slice(0, 7));
  const month = period && period.month !== 'ALL' ? period.month : localMonth;
  const setMonth = (next: string) => {
    setLocalMonth(next);
    period?.setMonth(next as OperationalMonth);
  };
  return [month, setMonth] as const;
}

function valueOf(line: CommissionClosingLine, key: SortKey): string | number {
  if (key === 'name') return line.resourceDisplayName;
  if (key === 'commission') return line.commissionCents;
  if (key === 'paid') return line.paidCents;
  if (key === 'outstanding') return line.outstandingCents;
  return key === 'advances' ? line.advancesCents : line.netCents;
}

function CloseDialog({
  closing,
  today,
  onClose,
  onClosed,
}: {
  closing: CommissionClosingView;
  today: string;
  onClose: () => void;
  onClosed: () => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const [dueOn, setDueOn] = useState<string | null>(null);
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  const lines = closing.lines.filter((line) => line.pendingCommissionCents > 0);

  async function confirm() {
    if (!dueOn) return setError('Escolha o vencimento.');
    setSaving(true);
    try {
      await commissionsApi.closeMonth({ month: closing.month, dueOn });
      snackbar.success(`Comissões de ${monthLabel(closing.month)} apuradas.`);
      await onClosed();
      onClose();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title={`Apurar comissões de ${monthLabel(closing.month)}`}
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" disabled={saving} onClick={() => void confirm()}>
            Gerar despesas
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <p className="text-content-faint">
          Gera uma despesa por profissional pela comissão, com competência em{' '}
          {monthLabel(closing.month)} e a conta do plano do vínculo. Os vales já saíram do caixa e
          abatem o que falta pagar. A baixa segue pela conciliação. Esta apuração pega só o que
          ainda não foi apurado: o que nascer depois fica para a próxima, que pode ser neste mesmo
          mês.
        </p>
        {error ? <RequestAlert message={error} /> : null}
        <ul className="grid gap-1">
          {lines.map((line) => (
            <li key={line.resourceId} className="flex flex-wrap justify-between gap-2">
              <span>{line.resourceDisplayName}</span>
              <span>
                {brl(line.pendingCommissionCents)} − {brl(line.pendingAdvancesCents)} ={' '}
                <strong>{brl(line.pendingNetCents)}</strong>
              </span>
            </li>
          ))}
        </ul>
        <DatePicker label="Vencimento" value={dueOn} onValueChange={setDueOn} today={today} />
      </div>
    </Modal>
  );
}

function LineSituation({ line }: { line: CommissionClosingLine }) {
  if (line.commissionCents === 0) return <StatusPill tone="neutral">Só vale</StatusPill>;
  if (line.pendingCommissionCents > 0) return <StatusPill tone="warning">A conferir</StatusPill>;
  if (line.outstandingCents === 0) return <StatusPill tone="positive">Quitada</StatusPill>;
  return line.paidCents > 0 ? (
    <StatusPill tone="warning">Paga em parte</StatusPill>
  ) : (
    <StatusPill tone="warning">Em aberto</StatusPill>
  );
}

const instant = (value: string) =>
  new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
    .format(new Date(value))
    .replace(', ', ' às ');

/// As apurações do mês, cada uma com a própria despesa e o próprio vencimento (ADR 0082, decisão
/// 33). Só a mais recente, sem pagamento e com o mês aberto pode ser desfeita (decisão 34).
function MonthClosings({
  closings,
  canClose,
  onUndo,
}: {
  closings: readonly CommissionClosingSummary[];
  canClose: boolean;
  onUndo: (closing: CommissionClosingSummary) => void;
}) {
  return (
    <section aria-labelledby="commission-month-closings" className="grid gap-2">
      <h3 id="commission-month-closings" className="font-semibold">
        Apurações deste mês
      </h3>
      <ul className="divide-y divide-border rounded-xl border border-border text-sm">
        {closings.map((closing) => (
          <li
            key={closing.id}
            className="flex flex-wrap items-start justify-between gap-3 px-3 py-2"
          >
            <span className="min-w-0">
              <span className="block font-medium">Apurada em {instant(closing.createdAt)}</span>
              <span className="block text-content-faint">
                Vencimento em {formatCivilDate(closing.dueOn)} ·{' '}
                {closing.professionals === 1
                  ? '1 profissional'
                  : `${String(closing.professionals)} profissionais`}
              </span>
              <span className="block">
                Comissão {brl(closing.commissionCents)} · Vales {brl(closing.advancesCents)} · Pago{' '}
                {brl(closing.paidCents)} · Falta pagar {brl(closing.outstandingCents)}
              </span>
              {canClose && closing.undoBlockedReason ? (
                <span className="block text-xs text-content-faint">
                  {closing.undoBlockedReason}
                </span>
              ) : null}
            </span>
            {canClose && !closing.undoBlockedReason ? (
              <Button type="button" variant="outline" onClick={() => onUndo(closing)}>
                Desfazer apuração
              </Button>
            ) : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function UndoClosingDialog({
  closing,
  onClose,
  onUndone,
}: {
  closing: CommissionClosingSummary;
  onClose: () => void;
  onUndone: () => Promise<void>;
}) {
  const snackbar = useSnackbar();
  const [error, setError] = useState<string>();
  const [saving, setSaving] = useState(false);
  async function confirm() {
    setSaving(true);
    try {
      await commissionsApi.undoClosing(closing.id);
      snackbar.success('Apuração desfeita.');
      await onUndone();
      onClose();
    } catch (cause) {
      setError(getApiError(cause));
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal
      open
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
      title="Desfazer esta apuração?"
      footer={
        <>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" disabled={saving} onClick={() => void confirm()}>
            Desfazer apuração
          </Button>
        </>
      }
    >
      <div className="grid gap-3 text-sm">
        <p>
          As despesas desta apuração saem de Despesas, {brl(closing.commissionCents)} de comissão
          voltam a esperar apuração e {brl(closing.advancesCents)} voltam ao saldo de vales.
        </p>
        {error ? <RequestAlert message={error} /> : null}
      </div>
    </Modal>
  );
}

type BalanceSort = 'name' | 'owed' | 'open';

/// Quanto falta pagar a cada profissional, somando os meses, sem procurar em Despesas (ADR 0082,
/// decisão 30). O que ainda não foi apurado fica à parte, porque ainda pode mudar.
function CommissionBalances({
  onOpenExpenses,
}: {
  onOpenExpenses?: ((professional: string) => void) | undefined;
}) {
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<BalanceSort>('name');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const query = useQuery({
    queryKey: ['company', 'commissions', 'closing', 'balances'],
    queryFn: commissionsApi.balances,
    retry: false,
  });
  const all = query.data ?? [];
  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    const sign = direction === 'asc' ? 1 : -1;
    const valueOfRow = (row: CommissionBalance) =>
      sort === 'owed' ? row.owedCents : sort === 'open' ? row.openCents : 0;
    return (query.data ?? [])
      .filter((row) => row.resourceDisplayName.toLocaleLowerCase('pt-BR').includes(term))
      .sort((a, b) =>
        sort === 'name'
          ? a.resourceDisplayName.localeCompare(b.resourceDisplayName, 'pt-BR') * sign
          : (valueOfRow(a) - valueOfRow(b)) * sign,
      );
  }, [direction, query.data, search, sort]);
  function toggle(column: BalanceSort) {
    if (column === sort) setDirection(direction === 'asc' ? 'desc' : 'asc');
    else {
      setSort(column);
      setDirection('asc');
    }
  }
  const sortable = { sort, direction, onToggle: toggle };
  const owedTotal = all.reduce((sum, row) => sum + row.owedCents, 0);
  return (
    <section aria-labelledby="commission-balances-title" className="grid gap-3">
      <h2 id="commission-balances-title" className="text-lg font-semibold">
        Quanto falta pagar: {brl(owedTotal)}
      </h2>
      {query.isLoading ? <LoadingCard label="Carregando o que falta pagar…" /> : null}
      {query.error ? <ErrorCard message={getApiError(query.error)} /> : null}
      {query.data && all.length === 0 ? (
        <p className="text-sm text-content-faint">Nada a pagar de comissão nesta unidade.</p>
      ) : null}
      {all.length > 0 ? (
        <>
          <ListFilterBar
            variant="panel"
            showViewPicker={false}
            searchLabel="Buscar profissional no saldo"
            search={search}
            onSearchChange={setSearch}
            count={`${String(rows.length)} ${rows.length === 1 ? 'profissional' : 'profissionais'}`}
          />
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[520px] text-sm">
              <caption className="sr-only">Quanto falta pagar a cada profissional</caption>
              <thead>
                <tr>
                  <SortableColumn column="name" label="Profissional" {...sortable} />
                  <SortableColumn column="owed" label="Falta pagar (apurado)" {...sortable} />
                  <SortableColumn column="open" label="Ainda não apurado" {...sortable} />
                  {onOpenExpenses ? (
                    <th scope="col" className="px-3 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.resourceId} className="border-t border-border align-top">
                    <td className="px-3 py-2 font-medium">{row.resourceDisplayName}</td>
                    <td className="px-3 py-2">{brl(row.owedCents)}</td>
                    <td className="px-3 py-2">
                      {brl(row.openCents)}
                      {row.advanceCarryCents > 0 ? (
                        <p className="text-xs text-content-faint">
                          {brl(row.advanceCarryCents)} de vale ainda a abater
                        </p>
                      ) : null}
                    </td>
                    {onOpenExpenses ? (
                      <td className="px-3 py-2 text-right">
                        <IconButton
                          label={`Ver despesas de ${row.resourceDisplayName}`}
                          icon={<ReceiptText className="size-4" />}
                          onClick={() => onOpenExpenses(row.resourceDisplayName)}
                        />
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </section>
  );
}

/// Apuração do mês na unidade (ADR 0082, decisões 14, 15, 22 e 24).
export function CommissionClosing({
  today,
  canClose,
  onOpenExpenses,
}: {
  today: string;
  canClose: boolean;
  /// Abre em Despesas as comissões de quem recebe. Ausente sem Financeiro ou sem permissão.
  onOpenExpenses?: ((professional: string) => void) | undefined;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [month, setMonth] = useCommissionMonth(today);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<SortKey>('name');
  const [direction, setDirection] = useState<'asc' | 'desc'>('asc');
  const [closing, setClosing] = useState(false);
  const [undoing, setUndoing] = useState<CommissionClosingSummary | null>(null);
  const [advanceFor, setAdvanceFor] = useState<{ id: string; displayName: string } | null>(null);

  const query = useQuery({
    queryKey: ['company', 'commissions', 'closing', month],
    queryFn: () => commissionsApi.closing(month),
    retry: false,
  });
  const data = query.data;
  const rows = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR');
    const sign = direction === 'asc' ? 1 : -1;
    return (data?.lines ?? [])
      .filter((line) => line.resourceDisplayName.toLocaleLowerCase('pt-BR').includes(term))
      .sort((a, b) => {
        const [left, right] = [valueOf(a, sort), valueOf(b, sort)];
        if (typeof left === 'number' && typeof right === 'number') return (left - right) * sign;
        return String(left).localeCompare(String(right), 'pt-BR') * sign;
      });
  }, [data, direction, search, sort]);

  function toggle(column: SortKey) {
    if (column === sort) setDirection(direction === 'asc' ? 'desc' : 'asc');
    else {
      setSort(column);
      setDirection('asc');
    }
  }
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: ['company', 'commissions', 'closing'] });

  function exportAll() {
    if (!data) return;
    const url = URL.createObjectURL(
      new Blob([commissionClosingCsv(data)], { type: 'text/csv;charset=utf-8' }),
    );
    const link = document.createElement('a');
    link.href = url;
    link.download = `comissoes-${data.month}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    snackbar.success(`Apuração exportada para comissoes-${data.month}.csv.`);
  }

  const sortable = { sort, direction, onToggle: toggle };
  const apurated = (data?.closings.length ?? 0) > 0;
  const hasPending = data?.lines.some((line) => line.pendingCommissionCents > 0) ?? false;
  return (
    <section className="grid gap-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Select label="Mês" value={month} options={monthOptions(today)} onValueChange={setMonth} />
        <Input
          label="Filtrar por profissional"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
        />
      </div>
      {query.isLoading ? <LoadingCard label="Carregando a apuração…" /> : null}
      {query.error ? <ErrorCard message={getApiError(query.error)} /> : null}
      {data && data.lines.length === 0 ? (
        <EmptyCard
          icon={HandCoins}
          title="Nenhuma comissão neste mês"
          description="A comissão aparece aqui quando nasce: na venda do produto ou do pacote e ao concluir o atendimento."
        />
      ) : null}
      {data && data.lines.length > 0 ? (
        <>
          <div className="overflow-x-auto rounded-xl border border-border">
            <table className="w-full min-w-[640px] text-sm">
              <caption className="sr-only">Comissões de {monthLabel(data.month)}</caption>
              <thead>
                <tr>
                  <SortableColumn column="name" label="Profissional" {...sortable} />
                  <SortableColumn column="commission" label="Comissão" {...sortable} />
                  <SortableColumn column="advances" label="Vales" {...sortable} />
                  <SortableColumn column="net" label="Líquido" {...sortable} />
                  {apurated ? (
                    <>
                      <SortableColumn column="paid" label="Pago" {...sortable} />
                      <SortableColumn column="outstanding" label="Falta pagar" {...sortable} />
                    </>
                  ) : null}
                  <th scope="col" className="px-3 py-2 text-left">
                    Situação
                  </th>
                  {apurated && onOpenExpenses ? (
                    <th scope="col" className="px-3 py-2">
                      <span className="sr-only">Ações</span>
                    </th>
                  ) : null}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-3 py-4 text-content-faint">
                      Ninguém encontrado com esse nome.
                    </td>
                  </tr>
                ) : null}
                {rows.map((line) => (
                  <tr key={line.resourceId} className="border-t border-border align-top">
                    <td className="px-3 py-2">
                      <p className="font-medium">{line.resourceDisplayName}</p>
                      <p className="text-xs text-content-faint">
                        {accountLabelOf(line.accountCode)}
                      </p>
                      <ul className="mt-1 grid gap-0.5 text-xs text-content-faint">
                        {line.parts.map((part) => (
                          <li key={part.id}>
                            {part.lineDisplayName}
                            {part.role === 'ASSISTANT' ? ' (assistente)' : ''}:{' '}
                            {brl(part.amountCents)}
                          </li>
                        ))}
                      </ul>
                      {canClose ? (
                        <span className="mt-2 flex items-center gap-1">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={() =>
                              setAdvanceFor({
                                id: line.resourceId,
                                displayName: line.resourceDisplayName,
                              })
                            }
                          >
                            Registrar vale
                          </Button>
                          <HelpTip label="Ajuda: o que é o vale" content={commissionAdvanceHelp} />
                        </span>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">{brl(line.commissionCents)}</td>
                    <td className="px-3 py-2">{brl(line.advancesCents)}</td>
                    <td className="px-3 py-2">
                      {brl(line.netCents)}
                      {line.carryCents > 0 ? (
                        <p className="text-xs text-content-faint">
                          {brl(line.carryCents)} passa ao próximo mês
                        </p>
                      ) : null}
                    </td>
                    {apurated ? (
                      <>
                        <td className="px-3 py-2">{brl(line.paidCents)}</td>
                        <td className="px-3 py-2">
                          {brl(line.outstandingCents)}
                          {line.pendingCommissionCents > 0 ? (
                            <p className="text-xs text-content-faint">
                              {brl(line.pendingCommissionCents)} ainda sem apurar
                            </p>
                          ) : null}
                        </td>
                      </>
                    ) : null}
                    <td className="px-3 py-2">
                      <LineSituation line={line} />
                    </td>
                    {apurated && onOpenExpenses ? (
                      <td className="px-3 py-2 text-right">
                        {line.commissionCents > line.pendingCommissionCents ? (
                          <IconButton
                            label={`Ver despesa de ${line.resourceDisplayName}`}
                            icon={<ReceiptText className="size-4" />}
                            onClick={() => onOpenExpenses(line.resourceDisplayName)}
                          />
                        ) : null}
                      </td>
                    ) : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {apurated ? (
            <MonthClosings closings={data.closings} canClose={canClose} onUndo={setUndoing} />
          ) : null}
          <div className="grid gap-1 text-sm">
            <h3 className="font-semibold">Comissão por conta do plano</h3>
            {data.totals.byAccount.map((total) => (
              <p key={total.accountCode ?? 'none'} className="flex justify-between gap-2">
                <span>{accountLabelOf(total.accountCode)}</span>
                <span>{brl(total.cents)}</span>
              </p>
            ))}
            {data.totals.offPayrollCents > 0 ? (
              <>
                <p className="mt-2 flex justify-between gap-2">
                  <span>CLT na folha</span>
                  <span>{brl(data.totals.inPayrollCents)}</span>
                </p>
                <p className="flex justify-between gap-2">
                  <span>CLT fora da folha</span>
                  <span>{brl(data.totals.offPayrollCents)}</span>
                </p>
              </>
            ) : null}
          </div>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button type="button" variant="outline" onClick={exportAll}>
              Exportar tudo
            </Button>
            {data.status === 'CLOSED' ? (
              <p className="text-sm">Tudo apurado neste mês. As despesas estão em Despesas.</p>
            ) : canClose && hasPending && data.canGenerateExpenses && !data.monthClosed ? (
              <Button type="button" onClick={() => setClosing(true)}>
                Apurar comissões de {monthLabel(data.month)}
              </Button>
            ) : null}
          </div>
          {data.monthClosed && hasPending ? (
            <p role="status" className="text-sm font-medium text-warning">
              Este mês já foi fechado na contabilidade. Reabra o mês para apurar as comissões.
            </p>
          ) : null}
          <p className="text-xs text-content-faint">
            {data.canGenerateExpenses
              ? 'Dá para apurar mais de uma vez no mês: cada apuração pega só o que ainda não foi apurado. Apure tudo antes do Fechamento contábil, porque o que ficar de fora entra na apuração de um mês seguinte.'
              : 'Sem o módulo Financeiro, esta tela fica só como relatório.'}
          </p>
        </>
      ) : null}
      {undoing ? (
        <UndoClosingDialog closing={undoing} onClose={() => setUndoing(null)} onUndone={refresh} />
      ) : null}
      {closing && data ? (
        <CloseDialog
          closing={data}
          today={today}
          onClose={() => setClosing(false)}
          onClosed={refresh}
        />
      ) : null}
      {advanceFor ? (
        <CommissionAdvanceDialog
          resource={advanceFor}
          today={today}
          onClose={() => setAdvanceFor(null)}
          onSaved={refresh}
        />
      ) : null}
    </section>
  );
}

/// O que o profissional vê de si (decisão 17).
export function MyCommissions({ today }: { today: string }) {
  const [month, setMonth] = useCommissionMonth(today);
  const query = useQuery({
    queryKey: ['company', 'commissions', 'mine', month],
    queryFn: () => commissionsApi.mine(month),
    retry: false,
  });
  const mine = query.data;
  return (
    <section className="grid gap-4">
      <Select label="Mês" value={month} options={monthOptions(today)} onValueChange={setMonth} />
      {query.isLoading ? <LoadingCard label="Carregando suas comissões…" /> : null}
      {query.error ? <ErrorCard message={getApiError(query.error)} /> : null}
      {mine?.status === 'DISABLED' ? (
        <EmptyCard
          icon={HandCoins}
          title="Consulta ainda não liberada"
          description="A empresa ainda não liberou esta consulta para a unidade."
        />
      ) : null}
      {mine?.status === 'NO_PROFESSIONAL' ? (
        <EmptyCard
          icon={HandCoins}
          title="Seu acesso não está ligado a um profissional"
          description="Peça à empresa para ligar seu acesso ao seu cadastro de profissional nesta unidade."
        />
      ) : null}
      {mine?.status === 'READY' ? (
        <>
          <dl className="grid gap-3 sm:grid-cols-3">
            {[
              ['Comissão', mine.commissionCents],
              ['Vales', mine.advancesCents],
              ['Líquido', mine.netCents],
            ].map(([label, cents]) => (
              <div key={String(label)} className="rounded-xl border border-border p-3">
                <dt className="text-xs text-content-faint">{label}</dt>
                <dd className="text-xl font-semibold">{brl(Number(cents))}</dd>
              </div>
            ))}
          </dl>
          {mine.parts.length === 0 ? (
            <p className="text-sm text-content-faint">Nenhuma comissão neste mês.</p>
          ) : (
            <ul className="grid gap-1 text-sm">
              {mine.parts.map((part) => (
                <li key={part.id} className="flex justify-between gap-2">
                  <span>
                    {part.lineDisplayName}
                    {part.role === 'ASSISTANT' ? ' (assistente)' : ''}
                  </span>
                  <span>{brl(part.amountCents)}</span>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}
    </section>
  );
}

export function CompanyCommissions({
  today,
  canRead,
  canClose,
  canReadOwn,
  onOpenExpenses,
}: {
  today: string;
  canRead: boolean;
  canClose: boolean;
  canReadOwn: boolean;
  onOpenExpenses?: ((professional: string) => void) | undefined;
}) {
  return (
    <section>
      <PageHeader
        trail="Vendas / Comissões"
        title={canRead ? 'Apuração do mês' : 'Minhas comissões'}
        description={
          canRead
            ? 'Veja quanto falta pagar a cada profissional, confira o mês e apure as comissões da unidade.'
            : 'O que você recebeu de comissão e os vales abatidos, mês a mês.'
        }
      />
      {canRead ? (
        <div className="grid gap-8">
          <CommissionBalances onOpenExpenses={onOpenExpenses} />
          <CommissionClosing today={today} canClose={canClose} onOpenExpenses={onOpenExpenses} />
        </div>
      ) : null}
      {canRead && canReadOwn ? (
        <div className="mt-8">
          <h2 className="mb-3 text-lg font-semibold">Minhas comissões</h2>
          <MyCommissions today={today} />
        </div>
      ) : null}
      {!canRead && canReadOwn ? <MyCommissions today={today} /> : null}
      <BackToTop />
    </section>
  );
}
